/**
 * Sub-aba "Gift Cards": emite o código de presente, avisa por e-mail quem recebe, e lista os
 * emitidos com a situação de cada um. Porte de `admin.html:2116-2149` e `7124-7283`; linhas 74 a 79
 * do inventário.
 *
 * O QUE FICA IGUAL MESMO PARECENDO DEFEITO:
 *   - "Hoje" é a data em UTC (`new Date().toISOString().slice(0, 10)`, `admin.html:7235` e `7250`):
 *     à noite no Brasil, o mínimo do seletor e a checagem de validade já são o dia de amanhã.
 *   - O gift card vale "Expirado" já no DIA da validade, porque a meia-noite daquele dia é anterior
 *     a agora (`admin.html:7170`).
 *   - O e-mail sai sem esperar a resposta, e a falha dele não aparece para ninguém (`admin.html:7265`).
 */

// Estado, memória, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças da plataforma.
import { Botao, Campo } from '@complexity/ui';
// As chamadas da área.
import {
  excluirGiftCard,
  inserirGiftCard,
  listarGiftCards,
  notificarGiftCardEmitido,
} from '@studiolu/dados/areas/sistema';
// O nome do lead que resgatou vem da lista de leads, lida como o Kanban a lê.
import { listarLeads } from '@studiolu/dados/areas/vendas';
// As linhas de `gift_cards` e `leads_funil`.
import type { LinhaGiftCards, LinhaLeadsFunil } from '@studiolu/dados';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O parágrafo do status, desenhado como o legado.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// A memória da área (campos e status que sobrevivem à ida a outra área) e o mínimo da validade.
import { MINIMO_DA_VALIDADE, useMemoria, useStatusDaArea } from './memoria.js';
// A moeda do legado.
import { mascararMoedaLegado, parseMoeda } from '../../comum/moeda.js';
import { formatarMoeda } from '../../comum/formatos.js';
// O botão de copiar código do legado.
import { BotaoCopiarCodigo } from '../../comum/copiar-codigo.js';
// Hoje em UTC, a data de banco em DD/MM/AAAA, e a linha compacta.
import { LinhaCompacta, dataBR, dataHoraBR, hojeUTC } from '../financeiro/apoio.js';
// As peças visuais da área.
import { BotaoDaLinha, CartaoParam, DestaqueAlerta, LinhaParam, NotaSecao, TextoParam, type PropsDaSubAba } from './pecas.js';

// 32 letras e números, sem 0, O, 1 e I, que se confundem na hora de digitar (`admin.html:7133`).
const ALFABETO_DO_CODIGO = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
// Quantas vezes tentar um código novo quando o sorteado já existe (`admin.html:7256`).
const TENTATIVAS_DE_CODIGO = 5;

/** `LU-` e 6 caracteres sorteados (`gerarCodigoGiftCard`, `admin.html:7132`). */
function gerarCodigoGiftCard(): string {
  let codigo = 'LU-';
  // 6 caracteres: o tamanho do código do legado.
  for (let i = 0; i < 6; i++) codigo += ALFABETO_DO_CODIGO[Math.floor(Math.random() * ALFABETO_DO_CODIGO.length)];
  return codigo;
}

/** A situação de um gift card, com o tom (`statusGiftCard`, `admin.html:7164-7174`). */
function situacao(gc: LinhaGiftCards, leads: readonly LinhaLeadsFunil[]): { texto: string; tom: 'sucesso' | 'erro' | 'neutro' } {
  if (gc.resgatado_em) {
    const lead = leads.find((l) => l.id === gc.lead_id_resgate);
    // "Resgatado em DD/MM/AAAA · Nome", e o nome só quando o lead está na lista.
    return { texto: `Resgatado em ${dataHoraBR(gc.resgatado_em)}${lead ? ' · ' + (lead.nome || '(sem nome)') : ''}`, tom: 'sucesso' };
  }
  // A validade lida à meia-noite local, comparada com agora.
  if (gc.validade && new Date(gc.validade + 'T00:00:00') < new Date()) return { texto: 'Expirado', tom: 'erro' };
  return { texto: 'Ativo', tom: 'neutro' };
}

// A cor de cada tom, pelos tokens (`.giftcard-resgatado` e `.giftcard-expirado`, `admin.html:1231-1232`).
const COR_DO_TOM = { sucesso: 'font-semibold text-sucesso', erro: 'font-semibold text-erro', neutro: 'text-texto-suave' } as const;

export function SubAbaGiftCards({ confirmar, alertar, recarga }: PropsDaSubAba) {
  const { dados } = usePainel();
  // `null` enquanto lê: o "Carregando…" do legado (`admin.html:7156`).
  const [lista, setLista] = useState<readonly LinhaGiftCards[] | null>(null);
  // A última lista lida (`giftCardsCarregados`): falha de leitura mantém a anterior.
  const carregados = useRef<readonly LinhaGiftCards[]>([]);
  // Os leads, para o nome de quem resgatou (`leadsCarregados`, lido uma vez, como no login do legado).
  const [leads, setLeads] = useState<readonly LinhaLeadsFunil[]>([]);
  // O formulário, na memória da área: no legado são `input` fixos da página (`admin.html:2122-2146`),
  // e ir a outra aba e voltar encontra o que foi digitado.
  const [comprador, setComprador] = useMemoria('sistema:giftcard:comprador', '');
  const [destinatario, setDestinatario] = useMemoria('sistema:giftcard:destinatario', '');
  const [emailEnvio, setEmailEnvio] = useMemoria('sistema:giftcard:email', '');
  const [valor, setValor] = useMemoria('sistema:giftcard:valor', '');
  const [validade, setValidade] = useMemoria('sistema:giftcard:validade', '');
  // O parágrafo `#giftcard-status`, fora da lista no legado (`admin.html:2147`).
  const [status, mostrarStatus] = useStatusDaArea('sistema:giftcard:status');

  // Troca a lista e a referência juntas.
  const guardar = useCallback((nova: readonly LinhaGiftCards[]) => {
    carregados.current = nova;
    setLista(nova);
  }, []);

  // `carregarGiftCards` (`admin.html:7154-7162`).
  const carregar = useCallback(async () => {
    setLista(null);
    const resultado = await listarGiftCards(dados);
    // Falha só vai ao console, e a lista anterior fica (`admin.html:7150`).
    if (!resultado.ok) console.error('Erro ao carregar gift cards:', resultado.erro.mensagem);
    guardar(resultado.ok ? (resultado.dados ?? []) : carregados.current);
  }, [dados, guardar]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  // Os leads, uma vez ao abrir; falha deixa a lista vazia, e o resgate aparece sem nome.
  useEffect(() => {
    let vivo = true;
    void listarLeads(dados).then((resultado) => {
      if (vivo && resultado.ok) setLeads(resultado.dados ?? []);
    });
    return () => {
      vivo = false;
    };
  }, [dados]);

  // "Excluir" (`admin.html:7215-7229`). O id é número: a coluna é `bigint`, e o `parseInt` vale.
  const excluir = useCallback(
    async (id: number) => {
      const gc = carregados.current.find((g) => g.id === id);
      // A segunda trava: resgatado não se exclui, mesmo que o botão tenha ficado na tela.
      if (gc?.resgatado_em) {
        await alertar('Esse gift card já foi resgatado e está vinculado a um pagamento. Não pode ser excluído.');
        return;
      }
      if (!(await confirmar('Excluir esse gift card?'))) return;
      const resultado = await excluirGiftCard(dados, id);
      if (!resultado.ok) {
        await alertar('Erro ao excluir: ' + resultado.erro.mensagem);
        return;
      }
      guardar(carregados.current.filter((g) => g.id !== id));
    },
    [dados, confirmar, alertar, guardar],
  );

  // "Emitir" (`admin.html:7240-7283`).
  const emitir = useCallback(async () => {
    const nomeComprador = comprador.trim();
    const nomeDestinatario = destinatario.trim();
    const email = emailEnvio.trim();
    const valorEmNumero = parseMoeda(valor);
    const dataDeValidade = validade || null;

    if (!nomeComprador || !nomeDestinatario) {
      mostrarStatus('Preencha quem compra e quem recebe.');
      return;
    }
    if (!email) {
      mostrarStatus('Informe o e-mail que vai receber o código.');
      return;
    }
    if (!valorEmNumero) {
      mostrarStatus('Informe o valor.');
      return;
    }
    if (dataDeValidade && dataDeValidade < hojeUTC()) {
      mostrarStatus('A validade não pode ser anterior a hoje.');
      return;
    }
    mostrarStatus('Emitindo...');

    // Sorteia até 5 códigos, e só tenta de novo quando o sorteado já existe.
    let ultimoErro = '';
    for (let tentativas = 0; tentativas < TENTATIVAS_DE_CODIGO; tentativas++) {
      const codigo = gerarCodigoGiftCard();
      const resultado = await inserirGiftCard(dados, {
        codigo,
        valor: valorEmNumero,
        nome_comprador: nomeComprador,
        nome_destinatario: nomeDestinatario,
        validade: dataDeValidade,
        email_envio: email,
      });
      if (resultado.ok) {
        // O novo no topo da lista.
        guardar([resultado.dados, ...carregados.current]);
        // O e-mail "Você ganhou um Gift Card!", sem esperar a resposta, como no legado.
        void notificarGiftCardEmitido(dados, {
          email_envio: email,
          nome_destinatario: nomeDestinatario,
          codigo,
          valor: valorEmNumero,
          validade: dataDeValidade,
        });
        // Os cinco campos voltam vazios.
        setComprador('');
        setDestinatario('');
        setEmailEnvio('');
        setValor('');
        setValidade('');
        mostrarStatus(`Emitido e enviado por e-mail! Código: ${codigo}`);
        return;
      }
      ultimoErro = resultado.erro.mensagem;
      // Só a colisão do código (chave única) justifica outra tentativa (`admin.html:7277-7279`).
      if (!/duplicate key|unique/i.test(ultimoErro)) break;
    }
    mostrarStatus('Erro ao emitir: ' + ultimoErro);
  }, [
    comprador,
    destinatario,
    emailEnvio,
    valor,
    validade,
    dados,
    guardar,
    mostrarStatus,
    setComprador,
    setDestinatario,
    setEmailEnvio,
    setValor,
    setValidade,
  ]);

  return (
    <div className="flex flex-col gap-cx-4">
      {/* As cinco notas fixas (`admin.html:2117-2121`). */}
      <div className="flex flex-col gap-cx-2">
        <NotaSecao>
          Emita um código pra quem for presentear um serviço. A cobrança continua manual (Pix/transferência com a Lu), aqui só
          fica registrado o valor, quem comprou, pra quem, e se já foi resgatado. O resgate em si acontece no lead (aba Vendas),
          não aqui.
        </NotaSecao>
        <NotaSecao>
          <strong>Quem compra ≠ quem recebe:</strong> num presente típico, uma pessoa paga (comprador) pra outra usar o serviço
          (destinatário), ex: marido compra Coloração Pessoal pra esposa. O comprador pode nunca virar cliente; geralmente é o
          destinatário quem vira lead e resgata o código. Guardar os dois nomes separadamente ajuda a saber quem gastou (pra
          agradecer) e quem vai efetivamente usar.
        </NotaSecao>
        <NotaSecao>
          <strong>Uso único:</strong> depois de vincular um código a um lead, ele fica fechado. Não dá pra resgatar de novo. É um
          voucher pessoal pra uma pessoa específica, não um cupom de desconto reutilizável.
        </NotaSecao>
        <NotaSecao>
          <strong>Sem produto vinculado na emissão:</strong> o gift card não trava em qual serviço vai ser usado. Isso só se
          decide depois, quando o lead que resgatou entra no atendimento e você marca os produtos dele normalmente.
        </NotaSecao>
        <NotaSecao>
          <strong>E-mail de envio:</strong> ao emitir, o código e as instruções de uso são enviados automaticamente pro e-mail
          informado. <DestaqueAlerta>Use o e-mail de quem recebe (destinatário)</DestaqueAlerta>. O texto do e-mail já vem todo
          formatado pra essa pessoa (&quot;Você ganhou um Gift Card!&quot;), então mandar pro comprador soaria estranho. Esse
          e-mail é usado só pra esse envio, não fica guardado pra mais nada.
        </NotaSecao>
      </div>

      <LinhaCompacta>
        <Campo
          rotulo="Nome de quem compra"
          type="text"
          name="giftcard-nome-comprador"
          autoComplete="name"
          value={comprador}
          onChange={(evento) => setComprador(evento.target.value)}
        />
        <Campo
          rotulo="Nome de quem recebe"
          type="text"
          name="giftcard-nome-destinatario"
          autoComplete="name"
          value={destinatario}
          onChange={(evento) => setDestinatario(evento.target.value)}
        />
        <Campo
          rotulo="E-mail do destinatário"
          type="email"
          name="giftcard-email-envio"
          autoComplete="email"
          placeholder="e-mail do destinatário"
          value={emailEnvio}
          onChange={(evento) => setEmailEnvio(evento.target.value)}
        />
        <Campo
          rotulo="Valor"
          type="text"
          placeholder="R$ 0,00"
          value={valor}
          onChange={(evento) => setValor(mascararMoedaLegado(evento.target.value))}
        />
        {/* O mínimo do seletor é o dia da CARGA da página, em UTC, como o bloco do legado que roda
            uma vez só (`admin.html:7234-7238`): depois da meia-noite UTC ele continua oferecendo o
            dia de ontem, e quem recusa é o clique. */}
        <Campo
          rotulo="Validade (opcional)"
          type="date"
          min={MINIMO_DA_VALIDADE}
          value={validade}
          onChange={(evento) => setValidade(evento.target.value)}
        />
        <Botao onClick={() => void emitir()}>Emitir</Botao>
      </LinhaCompacta>
      <LinhaDeStatus texto={status} testid="status-giftcard" />

      {lista === null && <TextoParam>Carregando…</TextoParam>}
      {lista !== null && lista.length === 0 && <TextoParam>Nenhum gift card emitido ainda.</TextoParam>}
      {lista !== null && lista.length > 0 && (
        <CartaoParam testid="lista-giftcards">
          {lista.map((gc) => {
            const sit = situacao(gc, leads);
            return (
              <LinhaParam key={gc.id} data-id={String(gc.id)} className="md:items-start">
                <div className="min-w-0 flex-1 text-cx-sm">
                  <div className="flex flex-wrap items-center gap-x-cx-1">
                    <span className="font-mono text-cx-base font-semibold tracking-wide text-texto">{gc.codigo}</span>
                    <BotaoCopiarCodigo codigo={gc.codigo} />
                    <span>{`· ${formatarMoeda(Number(gc.valor))}`}</span>
                  </div>
                  {/* "De X pra Y", com a validade quando existe (`admin.html:7187` e `7198`). */}
                  <div className="mt-cx-1 text-texto-suave">
                    {`De ${gc.nome_comprador ?? ''} pra ${gc.nome_destinatario ?? ''}${gc.validade ? ' · válido até ' + dataBR(gc.validade) : ''}`}
                  </div>
                  <div className={`mt-cx-1 ${COR_DO_TOM[sit.tom]}`} data-testid="situacao-giftcard">
                    {sit.texto}
                  </div>
                </div>
                {/* "Excluir" só em gift card ainda não resgatado (`admin.html:7202`). */}
                <span className="ml-auto shrink-0 text-right">
                  {!gc.resgatado_em && <BotaoDaLinha perigo onClick={() => void excluir(gc.id)}>Excluir</BotaoDaLinha>}
                </span>
              </LinhaParam>
            );
          })}
        </CartaoParam>
      )}
    </div>
  );
}
